// src/pages/Homepage.jsx
import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const Homepage = () => {
  // Static image URLs
  const heroBg = "https://ziyaratguide.com/wp-content/uploads/2025/09/Al_Abbas_Mosque_Shrine_Karbala.jpg";
  const imamHussain = "https://ziyaratguide.com/wp-content/uploads/2025/09/566294861b2d1.jpg";
  const alAbbasShrine = "https://ziyaratguide.com/wp-content/uploads/2025/09/Al_Abbas_Mosque_Shrine_Karbala.jpg";
  const alMukhayamShrine = "https://ziyaratguide.com/wp-content/uploads/2025/09/Al-Mukhayam-Shrine.webp";
  const imamAliNajaf = "https://ziyaratguide.com/wp-content/uploads/2025/08/Holy-tomb-of-Imam-Ali-Najaf-Iraq-29.webp";

  return (
    <>
      <Helmet>
        <title>Ziyarat Guide | Explore Karbala, Najaf & Sacred Shrines</title>
        <meta
          name="description"
          content="Plan your ziyarat journey to Karbala and Najaf. Learn about Imam Hussain (AS), holy shrines, spiritual events, travel tips, and historical landmarks for pilgrims."
        />
        <meta property="og:title" content="Ziyarat Guide | Explore Karbala, Najaf & Sacred Shrines" />
        <meta
          property="og:description"
          content="Explore the spiritual heart of Iraq — Karbala and Najaf. Discover holy shrines, Imam Hussain’s legacy, and pilgrimage insights."
        />
        <meta
          property="og:image"
          content="https://ziyaratguide.com/wp-content/uploads/2025/09/Al_Abbas_Mosque_Shrine_Karbala.jpg"
        />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      {/* Hero Section */}
      <section 
        className="hero-section" 
        style={{ backgroundImage: `url(${heroBg})` }}
      >
        <h1>Welcome to Our Spiritual Journey</h1>
        <p>
          Explore the rich history and spiritual significance of the holy shrines and the teachings of Imam Mahdi (ajtf).
        </p>
        <div className="hero-buttons">
          <Link to="/shrines/shrine-of-two-sons-muslim-ibn-aqeel" className="primary-btn">
            Visit the Shrines
          </Link>
          <Link to="/imam-mahdi" className="secondary-btn">
            Learn About Imam Mahdi
          </Link>
        </div>
      </section>

      {/* Ziyarat Guide Introduction */}
      <section className="page-container my-8">
        <h2 className="page-title">Ziyarat Guide</h2>
        <p>
          This guide is dedicated to assisting those who wish to visit the sacred sites of Karbala. Whether you’re coming to perform Ziyarat for the first time or are a seasoned pilgrim, we aim to offer a comprehensive guide to help you understand the significance of each sacred location, along with practical advice for your journey.
        </p>
        <p>
          Our primary goal is to provide detailed information about the <strong>shrines</strong>, <strong>historical events</strong>, and <strong>landmarks</strong> in Karbala to enhance your spiritual experience. This guide includes a brief description of the locations, cultural practices, and sacred moments in the history of Imam Hussain’s (PBUH) legacy.
        </p>
      </section>

      {/* Featured Shrines Section */}
      <section className="featured-shrines-section">
        <h2 className="section-title text-center text-primary-dark mb-10">Featured Holy Shrines</h2>
        <div className="shrines-grid">

           {/* Shrine Card: Shrine of the Two Sons of Muslim Ibn Aqeel */}
          <div className="shrine-card">
            <img src="https://ziyaratguide.com/wp-content/uploads/2025/08/The-Two-Sons-of-Muslim-Ibn-Aqeel-Shrine.jpg" alt="Holy Tomb of Imam Ali Najaf" className="responsive-image" />
            <div className="shrine-card-content">
              <h3>Shrine of the Two Sons of Muslim Ibn Aqeel</h3>
              <p>The shrine is dedicated to Muhammad and Ibrahim, the two sons of Muslim Ibn Aqeel</p>
              <Link to="/shrines/shrine-of-two-sons-muslim-ibn-aqeel">Learn More &rarr;</Link>
            </div>
          </div>

          {/* Shrine Card: Al Mukhayam Shrine */}
          <div className="shrine-card">
            <img src={alMukhayamShrine} alt="Al Mukhayam Shrine" className="responsive-image" />
            <div className="shrine-card-content">
              <h3>Al Mukhayam Shrine</h3>
              <p>This site marks where Imam Hussain’s family set up camp before the battle, representing their suffering and resilience.</p>
              <Link to="/shrines/al-mukhayam-shrine">Learn More &rarr;</Link>
            </div>
          </div>

          {/* Shrine Card: Imam Hussain */}
          <div className="shrine-card">
            <img src={imamHussain} alt="Shrine of Imam Hussain" className="responsive-image" />
            <div className="shrine-card-content">
              <h3>Shrine of Imam Hussain</h3>
              <p>A sacred site and the burial place of Imam Hussain (as) in Karbala.</p>
              <Link to="/shrines/shrine-of-imam-hussain">Learn More &rarr;</Link>
            </div>
          </div>

         {/* Shrine Card: Al Abbas Shrine */}
          <div className="shrine-card">
            <img src={alAbbasShrine} alt="Al Abbas Shrine" className="responsive-image" />
            <div className="shrine-card-content">
              <h3>Al Abbas Shrine</h3>
              <p>The revered shrine of Hazrat Abbas (as), brother of Imam Hussain.</p>
              <Link to="/shrines/al-abbas-shrine">Learn More &rarr;</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Important Events for Pilgrims */}
      <section className="featured-shrines-section">
        <h2 className="section-title text-center text-primary-dark mb-10">
          Important Events for Pilgrims
        </h2>

        <div className="events-grid">
          {/* Ashura Commemoration */}
          <div className="shrine-card">
            <img 
              src={require("../assets/images/566294861b2d1.jpg")} 
              alt="Ashura Commemoration" 
              className="responsive-image" 
            />
            <div className="shrine-card-content">
              <h3>Ashura Commemoration</h3>
              <p>
                <strong>Significance:</strong> Ashura is a day of deep sorrow and reflection, where the sacrifice of Imam Hussain is remembered and honored by millions of Muslims around the world.
              </p>
              <p>
                <strong>Description:</strong> It marks the day of the martyrdom of Imam Hussain (PBUH), observed with mourning processions and recitation of elegies (Noha).
              </p>
              <Link to="/events/ashura" className="learn-more-link">
                Learn More →
              </Link>
            </div>
          </div>

          {/* Arbaeen Pilgrimage */}
          <div className="shrine-card">
            <img 
              src={require("../assets/images/150-4491751897813.jpeg")} 
              alt="Arbaeen Pilgrimage" 
              className="responsive-image" 
            />
            <div className="shrine-card-content">
              <h3>Arbaeen Pilgrimage</h3>
              <p>
                <strong>Description:</strong> Arbaeen takes place 40 days after Ashura and is the largest annual pilgrimage in the world. Pilgrims walk from across the globe to Karbala to honor Imam Hussain (PBUH).
              </p>
              <p>
                <strong>Significance:</strong> Arbaeen represents devotion, unity, and love for the values of justice and sacrifice upheld by Imam Hussain.
              </p>
              <Link to="/events/arbaeen" className="learn-more-link">
                Learn More →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Landmarks and Hotels in Karbala */}
      <section className="page-container my-8">
        <h2 className="page-title">Landmarks and Hotels in Karbala</h2>
        <div className="grid md:grid-cols-2 gap-8">
          {/* Landmarks */}
          <div className="bg-white p-6 rounded-lg shadow-md border-t-4 border-primary-dark">
            <h3 className="text-xl font-bold text-primary-dark mb-4">Key Landmarks</h3>
            <ul className="list-disc list-inside text-text-muted space-y-2">
              <li>
                <strong>Tal Al Zainabeiah Site:</strong> A sacred site dedicated to Zainab bint Ali, the sister of Imam Hussain, who played a key role in protecting the family during the events of Karbala.
              </li>
              <li>
                <strong>Al-Ukhaidir Fortress:</strong> A historical fortress located outside Karbala, with significant ties to Islamic history.
              </li>
            </ul>
          </div>

          {/* Hotels */}
          <div className="bg-white p-6 rounded-lg shadow-md border-t-4 border-primary-dark">
            <h3 className="text-xl font-bold text-primary-dark mb-4">Hotel Options in Karbala:</h3>
            <ul className="list-disc list-inside text-text-muted space-y-2">
              <li>Karbala Rayhaan Hotel</li>
              <li>Memory Islam Museum Hotel</li>
              <li>The Baron Hotel</li>
              <li>Coral Karbala Hotel</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Tips for Pilgrims */}
      <section className="page-container my-8">
        <h2 className="page-title">Tips for Pilgrims</h2>
        <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-accent-color">
          <ul className="list-disc list-inside text-text-muted space-y-3">
            <li>
              <strong>Plan for Peak Times:</strong> During major events like Ashura and Arbaeen, the city experiences heavy crowds. It’s recommended to plan ahead and arrive early to avoid congestion.
            </li>
            <li>
              <strong>Respect the Sanctity:</strong> Always maintain respectful behavior and attire when visiting the holy sites.
            </li>
            <li>
              <strong>Stay Hydrated:</strong> Karbala can be hot, especially in the summer months. Drink plenty of water and protect yourself from the sun.
            </li>
          </ul>
        </div>
      </section>

      {/* Interactive Features for Pilgrims */}
      <section className="page-container my-8">
        <h2 className="page-title">Interactive Features for Pilgrims</h2>
        <div className="bg-white p-6 rounded-lg shadow-md border-r-4 border-primary-light text-center">
          <ul className="list-none text-text-muted space-y-3">
            <li>
              <strong className="text-primary-dark">Mourning and Prayer Sessions:</strong> Participate in traditional mourning and prayer rituals held daily at the shrines.
            </li>
            <li>
              <strong className="text-primary-dark">Spiritual Reflection:</strong> Take time to reflect on the values of justice, loyalty, and sacrifice exemplified by Imam Hussain (PBUH) and his companions.
            </li>
            <li>
              <strong className="text-primary-dark">Educational Tours:</strong> Explore the rich Islamic history through guided tours available at key landmarks in Karbala.
            </li>
          </ul>
        </div>
      </section>

      {/* Short Imam Mahdi Section */}
      <section className="imam-mahdi-section">
        <h2>The Awaited Imam Mahdi (ajtf)</h2>
        <p>
          Delve into the profound topic of Imam Mahdi's return, His role as the Ultimate Reformer, and how to connect with His teachings.
        </p>
        <Link to="/imam-mahdi">
          Explore Imam Mahdi's Teachings
        </Link>
      </section>
    </>
  );
};

export default Homepage;
